import {Trans} from 'react-i18next'

import {
  Box,
  Card,
  Link,
  List,
  ListItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import JumboTitle from 'components/JumboTitle'
import {t} from 'i18next'

const StringOptions = () => {
  return (
    <JumboTitle
      title='String Options'
      description='Using react-i18next & Contenful'
    >
      <Box>
        <Typography variant='h4' sx={{mb: 2}}>
          <Trans i18nKey='budget_wallet_string_options_options_with_trans_component' />
        </Typography>
        <List>
          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  <Trans i18nKey='keyOne' values={{value: 'foo/bar'}} />
                </Typography>

                <Typography variant='body1' mt={3}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {"<Trans i18nKey='keyOne' values={{value: 'foo/bar'}} />"}
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"keyOne": "This string has a variable with value: {{value}}"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <Trans i18nKey='keyOne' values={{value: 'foo/bar'}}>
                    {'This string has a variable with value: {{value}}'}
                  </Trans>
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  <Trans
                    i18nKey='keyTwo'
                    values={{bold: 'bold', italic: 'italic'}}
                  />
                </Typography>
                <Typography
                  variant='body1'
                  mt={2}
                  sx={{fontSize: '0.875rem', color: 'text.secondary'}}
                >
                  (
                  <strong>
                    <i>strong</i>
                  </strong>
                  ,{' '}
                  <strong>
                    <i>i</i>
                  </strong>
                  ,{' '}
                  <strong>
                    <i>p</i>
                  </strong>
                  ,{' '}
                  <strong>
                    <i>br</i>
                  </strong>{' '}
                  supported by i18next)
                </Typography>

                <Typography variant='body1' mt={3}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      "<Trans i18nKey='keyTwo' values={{bold: 'bold', italic: 'italic'}} />"
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"keyTwo": "This string has a <strong>string in {{bold}}</strong> and a <i>string in {{italic}}</i>"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <Trans
                    i18nKey='keyTwo'
                    values={{bold: 'bold', italic: 'italic'}}
                  >
                    {
                      'This string has a <strong>string in {{bold}}</strong> and a <i>string in {{italic}}</i>"'
                    }
                  </Trans>
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  <Trans
                    i18nKey='keyThree'
                    components={[
                      <a
                        href='https://example.com'
                        target='_blank'
                        rel='noreferrer'
                        style={{textDecoration: 'underline'}}
                      >
                        override
                      </a>,
                    ]}
                    values={{text: 'Example'}}
                  />
                </Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      "<Trans i18nKey='keyThree' components={[<a href='https://example.com' target='_blank' rel='noreferrer'>override</a>]} values={{text: 'Example'}} />"
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>{'"keyThree": "This is a link <0>{{text}}</0>"'}</code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <Trans
                    i18nKey='keyThree'
                    components={[
                      <a
                        href='https://example.com'
                        target='_blank'
                        rel='noreferrer'
                        style={{textDecoration: 'underline'}}
                      >
                        override
                      </a>,
                    ]}
                    values={{text: 'Example'}}
                  >
                    {'This is a link <0>{{text}}</0>'}
                  </Trans>
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  <Trans
                    i18nKey='keyFour'
                    components={[<CustomComponent title={t('keyFourTitle')} />]}
                  />
                </Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      "<Trans i18nKey='keyFour' components={[<CustomComponent title={t('keyFourTitle')} />]} />"
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"keyFour": "This is a string with a <0>custom component</0>"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <Trans
                    i18nKey='keyFour'
                    components={[<CustomComponent title={t('keyFourTitle')} />]}
                  >
                    {'This is a string with a <0>custom component</0>'}
                  </Trans>
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  <Trans
                    i18nKey='keyFive'
                    components={[
                      <CustomComponent title={t('keyFourTitle')} />,
                      <a
                        href='https://example.com'
                        target='_blank'
                        rel='noreferrer'
                        style={{textDecoration: 'underline'}}
                      >
                        override
                      </a>,
                    ]}
                  />
                </Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      "<Trans i18nKey='keyFive' components={[<CustomComponent title={t('keyFourTitle')} />, <a href='https://example.com' target='_blank' rel='noreferrer'>override</a>]} />"
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"keyFive": "This one instead has <0>one</0> and <1>another</1>"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <Trans
                    i18nKey='keyFive'
                    components={[
                      <CustomComponent title={t('keyFourTitle')} />,
                      <a
                        href='https://example.com'
                        target='_blank'
                        rel='noreferrer'
                        style={{textDecoration: 'underline'}}
                      >
                        override
                      </a>,
                    ]}
                  >
                    {'This one instead has <0>one</0> and <1>another</1>'}
                  </Trans>
                </Paper>
              </Stack>
            </Card>
          </ListItem>
          <ListItem>
            <Typography>
              Reference:
              <Link
                href='https://react.i18next.com/latest/trans-component'
                target='_blank'
              >
                https://react.i18next.com/latest/trans-component
              </Link>
            </Typography>
          </ListItem>
        </List>
      </Box>

      <Box mt={4}>
        <Typography variant='h4' sx={{mb: 2}}>
          <Trans i18nKey='budget_wallet_string_options_options_with_t_function' />
        </Typography>

        <List>
          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>{t('tKeyOne')}</Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>{"{t('tKeyOne')}"}</code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"tKeyOne": "This string is rendered using the T function"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  {t(
                    'budget_wallet_string_options_this_string_is_rendered_using'
                  )}
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  {t('tKeyTwo', {variabile: 'cappuccino'})}
                </Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>{"{t('tKeyTwo', {variabile: 'cappuccino'})}"}</code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"tKeyTwo": "T function with a variable with value: {{variabile}}"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  {t('T function with a variable with value: {{variabile}}', {
                    variabile: 'cappuccino',
                  })}
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography variant='h6'>
                  {t(
                    'budget_wallet_string_options_tkeythreethis_id_does_not_exist'
                  )}
                </Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      "{t('tKeyThree', 'This ID does not exist and shows the fallback text')}"
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>{'"tKey__Three": "..."'}</code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  {t(
                    'budget_wallet_string_options_tkeythreethis_id_does_not_exist'
                  )}
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem sx={{pb: 6}}>
            <Card variant='outlined' sx={{p: 4, backgroundColor: '#eeececff'}}>
              <Stack>
                <Typography
                  variant='h6'
                  component='span'
                  sx={{display: 'flex', alignItems: 'center', gap: 1}}
                >
                  {t('tKeyFour_one', {count: 1})}
                  <span>|</span>
                  {t('tKeyFour_other', {count: 2})}
                </Typography>

                <Typography variant='body1' mt={2}>
                  Consumer:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      "{t('tKeyFour_one', {count: 1})} | {t('tKeyFour_other', {count: 2})}"
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={2}>
                  Input from endpoint:
                </Typography>

                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  <code>
                    {
                      '"tKeyFour_one": "{{count}} singular string", "tKeyFour_other": "{{count}} strings using plural"'
                    }
                  </code>
                </Paper>

                <Typography variant='body1' mt={3}>
                  Result:
                </Typography>
                <Paper
                  elevation={2}
                  sx={{p: 2, mt: 2, backgroundColor: '#f5f5f5'}}
                >
                  {t('{{count}} singular string', {count: 1})} |{' '}
                  {t('{{count}} strings using plural', {count: 2})}
                </Paper>
              </Stack>
            </Card>
          </ListItem>

          <ListItem>
            <Typography>
              Reference:
              <Link
                href='https://www.i18next.com/translation-function/context'
                target='_blank'
              >
                https://www.i18next.com/translation-function/context
              </Link>
            </Typography>
          </ListItem>
        </List>
      </Box>
    </JumboTitle>
  )
}

export default StringOptions

const CustomComponent = ({
  children,
  title,
}: {
  children?: React.ReactNode
  title?: string
}) => (
  <Tooltip title={title || "I'm so special"}>
    <span style={{color: 'blue', fontWeight: 'bold'}}>{children}</span>
  </Tooltip>
)
